// Zen Garden skin: a straw-hat samurai walks a raked-sand path to the torii gate.
//   idle    - samurai rests by the torii; the stone lanterns glow softly
//   pending - samurai walks the raked sand; progress eases them toward the gate
//   failure - samurai freezes and a fallen boulder blocks the path
//   success - the torii lights, a green calm settles, cherry petals drift
import { makeScene, mix, type Palette, type SceneCtx } from '../scene/engine';
import type { SkinDef } from './types';

const colors = (g: (n: string) => string): Palette => ({
  skyTop: mix(g('--bg-raise'), g('--accent'), 0.10), skyBot: g('--bg-raise'),
  ground: g('--surface'), groundLo: g('--bg'), edge: g('--line-bright'), dash: g('--line'),
  accent: g('--accent'), green: g('--green'), red: g('--red'), amber: g('--amber'), gild: g('--gild'),
  torii: g('--gild'), toriiHi: mix(g('--gild'), '#ffffff', 0.25),
  robe: g('--accent'), robeHi: mix(g('--accent'), '#ffffff', 0.22), skin: '#e9cfa0',
  rock: '#7c8088', rockHi: '#9aa0a8', rockLo: '#565b63',
  flameHi: '#fff2c2', flameLo: g('--amber'), petal: '#f4b8cf', moss: g('--green'),
  trunk: '#5a4326', blossom: '#f4b8cf', blossomHi: '#ffd6e6', blossomLo: '#e493b4',
  hat: '#d9c48a', hatHi: '#efe0b0', hakama: '#39364f', scabbard: '#241d12', blade: '#e6ecf2',
  shrub: g('--green'), shrubHi: mix(g('--green'), '#ffffff', 0.25),
});

// Ambient cherry blossoms drifting down-and-across the whole scene.
function driftPetals(s: SceneCtx, C: Palette, f: number, count: number) {
  const { px, W, groundY: GY } = s;
  for (let i = 0; i < count; i++) {
    const speed = 2 + (i % 3);
    const x = ((i * 43 + f * speed) % (W + 10)) - 5;
    const y = (i * 9 + f * (1 + (i % 2))) % (GY + 4);
    px(x, y, 2, 2, i % 2 ? C.petal : C.blossomHi);
  }
}

// A cherry tree: gnarled trunk + a blossom canopy that sways a touch. `big` gives
// a fuller crown for foreground trees.
function sakura(s: SceneCtx, C: Palette, x: number, sway: number, big = false) {
  const { px, groundY: GY } = s; const ty = GY;
  px(x, ty - 9, 2, 9, C.trunk); px(x - 2, ty - 7, 2, 1, C.trunk); px(x + 2, ty - 8, 2, 1, C.trunk);
  const o = sway, w = big ? 14 : 12, top = big ? 19 : 17;
  px(x - (w / 2 | 0) + 1, ty - top, w, 7, C.blossom); px(x - (w / 2 | 0) + 2, ty - top - 1, w - 2, 2, C.blossomHi);
  px(x - (w / 2 | 0) - 1, ty - 13, 3, 3, C.blossomLo); px(x + (w / 2 | 0), ty - 14, 3, 3, C.blossomLo);
  px(x - 3 + o, ty - top + 1, 2, 2, C.blossomHi); px(x + 3 - o, ty - top + 2, 2, 2, C.blossomHi);
}

// Clipped moss shrubs dotted through the garden.
function shrub(s: SceneCtx, C: Palette, x: number) {
  const { px, groundY: GY } = s; const by = GY;
  px(x - 3, by - 2, 8, 3, C.shrub); px(x - 1, by - 4, 5, 3, C.shrub); px(x, by - 5, 3, 2, C.shrubHi);
  px(x - 4, by - 1, 2, 2, C.shrub); px(x + 4, by - 1, 2, 2, C.shrub);
}

// Raked-sand stepping stones.
function stones(s: SceneCtx, C: Palette) {
  const { px, groundY: GY } = s;
  for (const [x, mossy] of [[42, false], [70, true], [110, false], [134, true]] as [number, boolean][]) {
    px(x, GY + 8, 5, 2, C.rockLo); px(x, GY + 7, 3, 1, mossy ? C.moss : C.rock);
  }
}
function lantern(s: SceneCtx, C: Palette, x: number, f: number, lit: boolean) {
  const { px, groundY: GY } = s; const by = GY;
  px(x, by - 3, 4, 3, C.rock); px(x - 1, by - 9, 6, 3, C.rockHi);
  px(x, by - 7, 4, 4, C.rockLo);
  if (lit) { px(x + 1, by - 6 - f, 2, 3, C.flameLo); px(x + 1, by - 5 - f, 1, 1, C.flameHi); }
}
function torii(s: SceneCtx, C: Palette, green: boolean) {
  const { px, glow, groundY: GY, W } = s; const bx = W - 34, top = GY - 26;
  if (green) glow(bx - 8, top - 2, 34, GY - top + 6, C.green, 0.15);
  const col = green ? mix(C.torii, C.green, 0.45) : C.torii;
  px(bx, top, 4, GY - top, col); px(bx + 18, top, 4, GY - top, col);
  px(bx, top + 4, 22, 3, col); px(bx + 1, top + 4, 20, 1, C.toriiHi);
  px(bx - 3, top - 3, 28, 4, col); px(bx - 2, top - 3, 26, 1, C.toriiHi);
  px(bx - 4, top - 1, 2, 2, col); px(bx + 22, top - 1, 2, 2, col);
}
// Straw-hat (kasa) samurai with a katana at the hip.
function samurai(s: SceneCtx, C: Palette, x: number, walkF: number, resting: boolean) {
  const { px, groundY: GY } = s;
  const fy = GY + 2, bob = !resting && walkF % 2 ? -1 : 0, y = fy - 15 + bob;
  // feet / hakama hem
  if (resting) { px(x + 3, fy - 3, 3, 3, C.hakama); px(x + 6, fy - 3, 3, 3, C.hakama); }
  else { const st = walkF % 2 ? 1 : -1; px(x + 3, fy - 3, 3, 3, C.hakama); px(x + 6 + st, fy - 3, 3, 3, C.hakama); }
  // katana worn at the hip, angled back-down
  px(x, y + 8, 1, 6, C.scabbard); px(x - 1, y + 13, 1, 1, C.scabbard); px(x + 1, y + 8, 1, 1, C.gild); // saya + tsuba
  // torso robe + hakama skirt + obi
  px(x + 3, y + 6, 8, 5, C.robe); px(x + 3, y + 6, 8, 1, C.robeHi);
  px(x + 3, y + 10, 8, 4, C.hakama); px(x + 3, y + 10, 8, 1, mix(C.hakama, '#ffffff', 0.15));
  px(x + 6, y + 9, 2, 2, C.gild);
  // face, shadowed under the brim
  px(x + 4, y + 2, 6, 4, C.skin); px(x + 4, y + 2, 6, 1, mix(C.skin, '#000000', 0.35));
  // kasa: wide conical straw hat, narrowing to a peak
  px(x, y + 1, 14, 1, mix(C.hat, '#000000', 0.2));  // brim underside
  px(x + 1, y, 12, 1, C.hat);
  px(x + 2, y - 1, 10, 1, C.hat); px(x + 3, y - 2, 8, 1, C.hatHi);
  px(x + 4, y - 3, 6, 1, C.hat); px(x + 5, y - 4, 4, 1, C.hatHi); px(x + 6, y - 5, 2, 1, C.hat);
  // drawn katana in the right hand (visible), pose by state
  if (resting) {
    px(x + 11, y + 6, 2, 2, C.hakama); px(x + 11, y + 5, 3, 1, C.gild);  // hand + tsuba
    px(x + 12, y - 6, 1, 12, C.blade);                                    // blade held upright
  } else {
    px(x + 10, y + 7, 2, 2, C.hakama); px(x + 11, y + 5, 3, 1, C.gild);  // hand + tsuba
    px(x + 12, y - 4, 1, 10, C.blade); px(x + 13, y - 5, 1, 1, C.blade);  // blade raised up-forward
  }
}
function boulder(s: SceneCtx, C: Palette, x: number) {
  const { px, groundY: GY } = s; const y = GY - 8;
  px(x + 1, y + 3, 12, 7, C.rock); px(x + 2, y, 10, 4, C.rock); px(x + 3, y + 1, 8, 3, C.rockHi);
  px(x, y + 6, 1, 4, C.rockLo); px(x + 13, y + 6, 1, 4, C.rockLo);
  px(x + 3, y + 4, 3, 1, C.rockLo); px(x + 8, y + 6, 3, 1, C.rockLo);
  px(x + 2, y - 1, 3, 1, C.moss); px(x + 9, y, 2, 1, C.moss);
}
const draw = (C: Palette, s: SceneCtx) => {
  const { px, mix: m, W, H, groundY: GY, pathL: PL, state: st, heroX: hx, walkF, flameF } = s;
  const tint = st === 'failure' ? C.red : st === 'success' ? C.green : C.accent;
  const allDone = st === 'success';
  for (let y = 0; y < GY; y++) px(0, y, W, 1, m(C.skyTop, C.skyBot, y / GY));
  for (let y = GY; y < H; y++) px(0, y, W, 1, m(C.ground, C.groundLo, (y - GY) / (H - GY)));
  px(0, GY - 1, W, 2, C.edge);
  stones(s, C);
  shrub(s, C, 28); shrub(s, C, 64); shrub(s, C, 118);
  // a small grove of blossom trees
  sakura(s, C, 12, flameF, true); sakura(s, C, 48, walkF % 2); sakura(s, C, 84, flameF, true); sakura(s, C, 120, walkF % 2);
  // raked grooves along the path; trodden groove behind the samurai
  const progX = st === 'idle' ? PL : hx + 6;
  for (let x = PL; x < W - 8; x += 9) { const done = allDone || x < progX; px(x, GY + 4, 5, 1, done ? tint : C.dash); }
  px(PL, GY + 2, allDone ? W - 8 - PL : Math.max(0, progX - PL), 1, m(tint, C.ground, 0.35));
  lantern(s, C, 24, flameF, true); lantern(s, C, 156, flameF, true);
  torii(s, C, st === 'success');
  driftPetals(s, C, flameF + walkF, st === 'success' ? 14 : 7); // ambient blossoms, a flurry on success
  samurai(s, C, hx, walkF, st === 'idle');
  if (st === 'failure') boulder(s, C, hx + 13);
};

export const zen: SkinDef = {
  label: 'Zen Garden',
  PromoteScene: makeScene({ colors, draw }),
  lines: {
    pending: 'Breathe. The gate is near.',
    failure: 'A stone upon the path. We wait.',
    success: 'The gate opens. All is still. 🌸',
  },
};
